import {
  Box,
  Divider,
  Skeleton,
  type SkeletonProps,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { usePaymentData } from 'api/payment/payment.queries';
import { useSubmission, useSubmissionPrice } from 'api/submission';
import Warning from 'assets/icons/warning.svg';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { currencyFormat } from 'helpers/utils';
import { castArray } from 'lodash-es';
import { DateTime } from 'luxon';
import { type FC, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

interface PaymentSummaryInfoLineProps {
  isLoading: boolean;
  leftContent: ReactNode;
  rightContent: ReactNode;
  wrapperStyles?: SxProps<Theme>;
  skeletonOptions?: SkeletonProps;
}

interface PolicySummaryInfoRowProps {
  label: string;
  children: ReactNode;
  isLoading: boolean;
}

const PolicySummaryInfoRow: FC<PolicySummaryInfoRowProps> = ({ label, children, isLoading }) => {
  return (
    <Stack direction="row" gap={2} alignItems="center">
      <Typography
        variant="body2"
        sx={{ minWidth: 135, color: (theme) => theme.palette.customColors.darkGray }}
      >
        {label}
      </Typography>
      {isLoading ? <Skeleton width={200} height={40} /> : children}
    </Stack>
  );
};

const PaymentSummaryInfoLine = ({
  isLoading,
  leftContent,
  rightContent,
  wrapperStyles,
  skeletonOptions,
}: PaymentSummaryInfoLineProps) => (
  <Stack
    sx={[
      {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      ...castArray(wrapperStyles),
    ]}
  >
    {leftContent}

    {!isLoading ? rightContent : <Skeleton width={200} height={40} {...skeletonOptions} />}
  </Stack>
);

// eslint-disable-next-line complexity
const PaymentSummary = () => {
  const { id: submissionId } = useParams() as { id: string };

  const { data: submission, isLoading: isSubmissionLoading } = useSubmission(submissionId);

  const isAnnualPaymentPlan = submission?.data?.policy_data?.pol_pay_plan === 'Annual';

  const { data: submissionPrice, isFetching: isSubmissionPriceLoading } = useSubmissionPrice(
    submissionId,
    {
      include_discounted_price: isAnnualPaymentPlan,
    },
    { enabled: !!submission },
  );

  const { data: paymentData, isFetching: isPaymentDataLoading } = usePaymentData(submissionId);

  const invoice = Object.values(paymentData?.invoices ?? {})?.at(-1);

  const invoiceId = invoice?.display_id;

  const quoteId = submission?.quote_number || '';

  const policyTerms = submission?.policyTerms || '';

  const namedInsured = paymentData?.named_insured || '';

  const monthlyPayment = submissionPrice?.monthly_due
    ? currencyFormat(submissionPrice!.monthly_due).merged
    : '$0';

  const totalPayment = isAnnualPaymentPlan
    ? submissionPrice?.discounted_total_due
      ? currencyFormat(submissionPrice.discounted_total_due).merged
      : '$0'
    : submissionPrice?.total_due
      ? currencyFormat(submissionPrice.total_due).merged
      : '$0';

  const isLoading = isSubmissionPriceLoading || isPaymentDataLoading || isSubmissionLoading;

  const invoiceDueDate = DateTime.fromISO(invoice?.due_date!);

  const today = DateTime.now().startOf('day');

  const isInvoiceDateInFuture = invoiceDueDate > today;

  return (
    <Box mb={'48px'}>
      <Stack pb={5} gap={2}>
        <Typography variant="subtitle1" fontWeight={800}>
          Pay & Finalize
        </Typography>

        <Stack gap={1}>
          <PolicySummaryInfoRow label="Named Insured" isLoading={isLoading}>
            <Typography variant="body2" fontWeight={800}>
              {namedInsured}
            </Typography>
          </PolicySummaryInfoRow>
          <PolicySummaryInfoRow label="Policy Term" isLoading={isLoading}>
            <Typography variant="body2" fontWeight={600}>
              {policyTerms}
            </Typography>
          </PolicySummaryInfoRow>
          <PolicySummaryInfoRow label="Submission Number" isLoading={isLoading}>
            <Typography variant="body2" fontWeight={600}>
              {quoteId}
            </Typography>
          </PolicySummaryInfoRow>
        </Stack>
      </Stack>

      <Box>
        <Box pb={3}>
          <Stack gap={0.5} pb={'28px'}>
            <Typography
              variant="body1"
              sx={{ color: (theme) => theme.palette.customColors.darkGray }}
            >
              Total Due
            </Typography>
            {isLoading ? (
              <>
                <Skeleton width={200} height={40} />
                <Skeleton width={200} height={40} />
              </>
            ) : (
              <>
                <Typography
                  variant="price"
                  sx={{
                    '&.MuiTypography-root': {
                      fontSize: '32px',
                    },
                  }}
                >
                  {isAnnualPaymentPlan ? totalPayment : monthlyPayment}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.customColors.darkGray }}
                >
                  {isAnnualPaymentPlan
                    ? 'Entire annual premium'
                    : `Then ${monthlyPayment} per month`}
                </Typography>
              </>
            )}
          </Stack>

          <Stack gap={1}>
            <PaymentSummaryInfoLine
              isLoading={isLoading}
              leftContent={
                <PaymentSummaryInfoLine
                  isLoading={isLoading}
                  leftContent={
                    <Typography variant="body2" fontWeight={700}>
                      {isInvoiceDateInFuture
                        ? `Due ${invoiceDueDate.toFormat('MMMM dd, yyyy')}`
                        : 'Due Today'}
                    </Typography>
                  }
                  rightContent={
                    /**
                     * Only CC has an invoice this early: it mints the new-business invoice at quote
                     * acceptance, while EC mints invoices at issuance — which this very payment triggers.
                     * So on EC there is no number to name, and the label is dropped rather than printed
                     * empty or filled with the quote number (ENG-12183 CON-010).
                     */
                    invoiceId ? (
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.customColors.darkGray }}
                      >
                        (Invoice No. {invoiceId})
                      </Typography>
                    ) : null
                  }
                  wrapperStyles={{
                    gap: 0.5,
                  }}
                />
              }
              rightContent={
                <Typography variant="body2" fontWeight={800}>
                  {isAnnualPaymentPlan ? totalPayment : monthlyPayment}
                </Typography>
              }
              skeletonOptions={{
                width: 100,
              }}
            />

            {!isAnnualPaymentPlan && (
              <PaymentSummaryInfoLine
                isLoading={isLoading}
                leftContent={<Typography variant="body2">Then 11 monthly payments:</Typography>}
                rightContent={<Typography variant="body2">{monthlyPayment}</Typography>}
                skeletonOptions={{
                  width: 100,
                }}
              />
            )}

            <Divider sx={{ borderColor: (theme) => theme.palette.customColors.cream }} />

            <PaymentSummaryInfoLine
              isLoading={isLoading}
              leftContent={<Typography variant="body2">Over 12 months:</Typography>}
              rightContent={<Typography variant="body2">{totalPayment}</Typography>}
              skeletonOptions={{
                width: 100,
              }}
            />
          </Stack>
        </Box>

        <Stack flexDirection="row" alignItems="center" gap={1}>
          <SvgIconWrapper
            src={Warning}
            sxOverrides={{ 'svg path': { stroke: (theme) => theme.palette.customColors.blue } }}
          />
          <Typography variant="body2" fontWeight={700}>
            Cancel anytime. Refunds are pro-rata.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default PaymentSummary;
